import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import { useWizard } from "../../context/WizardContext";
import { useWizardStore } from "../../store/wizardStore";
import { bookLanguageDisplay } from "../../i18n/bookLanguageDisplay";
import WizardLayout from "../../components/Wizard/WizardLayout";
import AdminPageNavigator from "../../components/AdminPageNavigator";

const TOTAL_STEPS = 5;
const CURRENT_STEP = 4;
const MAIN_MAX = 2000;
const NOTES_MAX = 1000;
const STORY_TITLE_MAX = 120;

const StoryDetailsStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setStoryDetails } = useWizardStore();
  const {
    language,
    storyType,
    images,
    storyDescription,
    storyGuidelines,
    storyTitle,
    setStoryDescription,
    setStoryGuidelines,
    setStoryTitle,
  } = useWizard();

  const [mainText, setMainText] = useState(storyDescription);
  const [notesText, setNotesText] = useState(storyGuidelines);
  const [titleText, setTitleText] = useState(storyTitle);

  useEffect(() => {
    setMainText(storyDescription);
  }, [storyDescription]);

  useEffect(() => {
    setNotesText(storyGuidelines);
  }, [storyGuidelines]);

  useEffect(() => {
    setTitleText(storyTitle);
  }, [storyTitle]);

  const canContinue = mainText.trim().length > 0;

  const handleContinue = () => {
    if (!canContinue) return;
    const desc = mainText.trim();
    const notes = notesText;
    const title = titleText.trim().slice(0, STORY_TITLE_MAX);
    setStoryDetails({ description: desc, notes, storyTitle: title });
    setStoryDescription(desc);
    setStoryGuidelines(notes);
    setStoryTitle(title);
    navigate("/create/review");
  };

  const langLabel = bookLanguageDisplay(language, t);
  const typeLabel =
    storyType === "surrogacy"
      ? t("wizard.storyType.surrogacyName")
      : storyType === "general"
        ? t("wizard.storyType.generalName")
        : "—";

  return (
    <WizardLayout currentStep={CURRENT_STEP} totalSteps={TOTAL_STEPS} stageClassName="story-details-stage">
      <main id="main-content" tabIndex={-1} className="relative z-10 flex w-full flex-1 flex-col items-center justify-center outline-none">
        <div className="lang-card-root">
          <p className="lang-eyebrow">{t("wizard.step", { current: CURRENT_STEP, total: TOTAL_STEPS })}</p>
          <h1 className="lang-title">{t("wizard.details.title")}</h1>
          <AdminPageNavigator align="center" />
          <p className="lang-sub">{t("wizard.details.subtitle")}</p>
          <p className="story-details-tip">✨ {t("wizard.details.tip")}</p>

          <div className="summary-strip">
            <div className="sum-chip">
              <span className="sum-label">{t("wizard.storyType.languageLabel")}</span> {langLabel}
            </div>
            <div className="sum-chip">
              <span className="sum-label">{t("wizard.details.sumType")}</span> {typeLabel}
            </div>
            {images.length > 0 && (
              <div className="sum-chip">
                <span className="sum-label">{t("wizard.details.sumPhotos")}</span>
                <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
                  {images.length}/{10}
                </span>{" "}
                {t("wizard.photos.photosLabel")}
              </div>
            )}
          </div>

          <div style={{ marginBottom: "20px" }}>
            <div className="story-field-label">
              <span className="story-field-label-text">{t("wizard.details.mainLabel")}</span>
              <span
                className={`story-char-count${mainText.length > MAIN_MAX * 0.9 ? " warn" : ""}`}
                dir="ltr"
                style={{ unicodeBidi: "isolate" }}
              >
                {mainText.length} / {MAIN_MAX}
              </span>
            </div>
            <textarea
              className="story-textarea story-textarea-main"
              maxLength={MAIN_MAX}
              placeholder={t("wizard.details.mainPlaceholder")}
              value={mainText}
              onChange={(e) => setMainText(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <div className="story-field-label">
              <span className="story-field-label-text">{t("wizard.details.storyTitleLabel")}</span>
              <span className="story-char-count" dir="ltr" style={{ unicodeBidi: "isolate" }}>
                {titleText.length} / {STORY_TITLE_MAX}
              </span>
            </div>
            <input
              type="text"
              className="story-textarea story-title-input"
              maxLength={STORY_TITLE_MAX}
              placeholder={t("wizard.details.storyTitlePlaceholder")}
              value={titleText}
              onChange={(e) => setTitleText(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="story-details-notes-section" style={{ marginBottom: "24px" }}>
            <div className="story-field-label">
              <span className="story-field-label-text">{t("wizard.details.notesToggle")}</span>
              <span className="story-char-count" dir="ltr" style={{ unicodeBidi: "isolate" }}>
                {notesText.length} / {NOTES_MAX}
              </span>
            </div>
            <textarea
              className="story-textarea story-textarea-notes"
              maxLength={NOTES_MAX}
              placeholder={t("wizard.details.notesPlaceholder")}
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
            />
          </div>

          <hr style={{ border: "none", borderTop: "1px solid #f5e8d3", margin: "24px 0" }} />

          {images.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span style={{ fontSize: "19px", fontWeight: 700, color: "#2C3E50" }}>
                  {t("wizard.details.yourPhotos")}
                  <span style={{ color: "#4B5563", fontWeight: 500, opacity: 0.5, fontSize: "14px" }}>
                    {" "}
                    ({images.length})
                  </span>
                </span>
                <button
                  type="button"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#E07A52",
                    fontSize: "16px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/create/photos-upload")}
                >
                  {t("wizard.details.addMore")}
                </button>
              </div>
              <div className="story-photos-strip">
                {images.map((img, i) => (
                  <img
                    key={`${img.serverId || "img"}-${i}`}
                    src={img.preview || ""}
                    className="story-photo-thumb"
                    alt=""
                  />
                ))}
              </div>
            </div>
          )}

          <div className="wizard-nav-row">
            <button type="button" className="wizard-btn-back" onClick={() => navigate("/create/photos-upload")}>
              ← {t("wizard.common.back")}
            </button>
            <button type="button" className="wizard-btn-next" disabled={!canContinue} onClick={handleContinue}>
              {t("wizard.details.cta")} →
            </button>
          </div>
        </div>
      </main>
    </WizardLayout>
  );
};

export default StoryDetailsStep;
